import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Coins } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { creditsApi } from "@/lib/api";
import { useCredits } from "@/context/CreditsContext";
import { PAYMENTS } from "@/constants/testIds";
import copy from "@/data/payments-copy.json";

export default function PaymentStatus() {
  const [params] = useSearchParams();
  const purchaseId = params.get("purchase_id");
  const { refresh, openBuy } = useCredits();
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("processing"); // processing | success | failure
  const polling = useRef(true);

  useEffect(() => {
    if (!purchaseId) {
      setPhase("failure");
      return;
    }
    let attempts = 0;
    const tick = async () => {
      if (!polling.current) return;
      attempts += 1;
      try {
        const d = await creditsApi.purchase(purchaseId);
        setData(d);
        if (d.status === "paid") {
          polling.current = false;
          setPhase("success");
          refresh();
          return;
        }
        if (d.status === "failed" || d.status === "expired") {
          polling.current = false;
          setPhase("failure");
          return;
        }
      } catch {
        /* keep polling */
      }
      if (attempts >= 20) {
        polling.current = false;
        setPhase("failure");
        return;
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      polling.current = false;
    };
  }, [purchaseId, refresh]);

  return (
    <AppShell>
      <div data-testid={PAYMENTS.statusRoot} className="mx-auto max-w-md py-10 text-center">
        {phase === "processing" && (
          <div data-testid={PAYMENTS.processing}>
            <Loader2 size={44} className="mx-auto animate-spin text-brand-accent" />
            <h1 className="mt-5 font-display text-2xl font-semibold text-brand-text">{copy.processing.title}</h1>
            <p className="mt-2 text-brand-sec">{copy.processing.body}</p>
            <p className="mt-1 text-sm text-brand-muted">{copy.processing.note}</p>
          </div>
        )}

        {phase === "success" && (
          <div data-testid={PAYMENTS.success}>
            <CheckCircle2 size={48} className="mx-auto text-brand-accent" />
            <h1 className="mt-5 font-display text-3xl font-semibold text-brand-text">{copy.success.title}</h1>
            <p className="mt-2 text-brand-sec">{copy.success.creditsAdded}</p>
            <div className="mx-auto mt-6 max-w-xs rounded-panel border border-brand-line bg-brand-surface p-5 text-left">
              {data?.previousBalance != null && (
                <div className="flex items-center justify-between py-1 text-sm text-brand-sec">
                  <span>{copy.success.previousBalance}</span>
                  <span data-testid="payment-previous-balance">{data.previousBalance}</span>
                </div>
              )}
              <div className="flex items-center justify-between py-1 text-base font-semibold text-brand-text">
                <span className="flex items-center gap-2"><Coins size={16} className="text-brand-accent" /> {copy.success.newBalance}</span>
                <span data-testid={PAYMENTS.newBalance}>{data?.balance}</span>
              </div>
            </div>
            <p className="mt-3 text-xs text-brand-muted">{copy.success.noExpiry}</p>
            <Link
              to="/biblioteka"
              className="mt-6 inline-flex items-center justify-center rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on"
            >
              {copy.success.backCta}
            </Link>
          </div>
        )}

        {phase === "failure" && (
          <div data-testid={PAYMENTS.failure}>
            <XCircle size={48} className="mx-auto text-red-400" />
            <h1 className="mt-5 font-display text-2xl font-semibold text-brand-text">{copy.failure.title}</h1>
            <p className="mt-2 text-brand-sec">{copy.failure.body}</p>
            <button
              type="button"
              onClick={openBuy}
              className="mt-6 inline-flex items-center justify-center rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on"
            >
              {copy.failure.retryCta}
            </button>
            <Link to="/biblioteka" className="mt-3 block text-sm text-brand-sec hover:text-brand-text">
              Wróć do Promptivo
            </Link>
          </div>
        )}
      </div>
    </AppShell>
  );
}
